import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { LuPenLine, LuTag } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip';
import OutlineBadge from './OutlineBadge';
import LoadingEllipsisText from './LoadingEllipsisText';

export const ProcessingBadgeKind = {
	Tags: 'tags',
	Summary: 'summary',
} as const;

type ProcessingBadgeKind = (typeof ProcessingBadgeKind)[keyof typeof ProcessingBadgeKind];

interface ProcessingBadgeProps {
	kind: ProcessingBadgeKind;
}

const contentByKind = {
	[ProcessingBadgeKind.Tags]: {
		icon: <LuTag />,
		label: 'Tagging',
		tooltip: 'Bundle is generating tags for this record.',
	},
	[ProcessingBadgeKind.Summary]: {
		icon: <LuPenLine />,
		label: 'Writing',
		tooltip: 'Bundle is generating a summary for this record.',
	},
} as const;

/**
 * Badge shown while a background pipeline is generating data for the first time.
 * Uses OutlineBadge so it stays visually consistent with tag badges.
 * Tooltip explains what processing means so users aren't left wondering.
 */
const ProcessingBadge: React.FC<ProcessingBadgeProps> = (props) => {
	const { kind } = props;
	const content = contentByKind[kind];
	const tooltipContent = (
		<Flex flex={1} direction="column">
			<Text color="white" textStyle="body.sm" lineHeight="tall">
				{content.tooltip}
			</Text>
		</Flex>
	);

	return (
		<Tooltip content={tooltipContent} contentProps={{ whiteSpace: 'nowrap' }}>
			<OutlineBadge
				color="fg.subtle"
				borderColor="border.subtle"
				bg="transparent"
				fontWeight="normal"
				icon={content.icon}
				flexShrink={0}
				whiteSpace="nowrap"
			>
				<LoadingEllipsisText label={content.label} lineHeight="inherit" />
			</OutlineBadge>
		</Tooltip>
	);
};

export default ProcessingBadge;
